// =============================================================================
// Module Project - Repository: Notifications
// In-app notification management
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

export type NotificationType = 
  | 'assign'
  | 'unassign'
  | 'owner_change'
  | 'comment'
  | 'mention'
  | 'status_change'
  | 'status_terminal'
  | 'dependency_blocked'
  | 'dependency_resolved'
  | 'attachment_uploaded'
  | 'watcher_added'
  | 'due_date_changed'
  | 'due_date_approaching'
  | 'task_created'
  | 'task_deleted'
  | 'subtask_completed'
  | 'milestone_assigned'
  | 'milestone_completed'
  | 'project_member_added'
  | 'project_member_removed'
  | 'project_role_changed'
  | 'timesheet_approved'
  | 'timesheet_rejected'
  | 'timesheet_submitted'
  | 'priority_changed'
  | 'estimation_changed';

export type EntityType = 'task' | 'comment' | 'attachment' | 'dependency' | 'timesheet' | 'project' | 'milestone';

export interface ProjectNotification {
  id: string;
  orgId: string;
  projectId: string;
  recipientMemberId: string;
  type: NotificationType;
  entityType: EntityType;
  entityId: string;
  payload: Record<string, unknown>;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationPayload {
  taskId?: string;
  taskTitle?: string;
  actorName?: string;
  actorMemberId?: string;
  commentBody?: string;
  oldStatus?: string;
  newStatus?: string;
  fileName?: string;
  dependencyTaskTitle?: string;
  milestoneName?: string;
  projectName?: string;
  memberName?: string;
  roleName?: string;
  oldValue?: string;
  newValue?: string;
  // Timesheet-related
  periodStart?: string;
  periodEnd?: string;
  entryCount?: number;
  rejectionReason?: string;
  totalMinutes?: number;
  // Due date
  dueDate?: string;
  daysRemaining?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH NOTIFICATIONS FOR CURRENT USER
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchNotifications(
  limit: number = 50,
  unreadOnly: boolean = false
): Promise<ProjectNotification[]> {
  let query = supabase
    .from('project_notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (unreadOnly) {
    query = query.eq('is_read', false);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }

  return (data || []).map(mapNotification);
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH NOTIFICATIONS FOR A PROJECT
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchProjectNotifications(
  projectId: string,
  limit: number = 50
): Promise<ProjectNotification[]> {
  const { data, error } = await supabase
    .from('project_notifications')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching project notifications:', error);
    return [];
  }

  return (data || []).map(mapNotification);
}

// ─────────────────────────────────────────────────────────────────────────────
// COUNT UNREAD NOTIFICATIONS
// ─────────────────────────────────────────────────────────────────────────────
export async function countUnreadNotifications(): Promise<number> {
  const { count, error } = await supabase
    .from('project_notifications')
    .select('*', { count: 'exact', head: true })
    .eq('is_read', false);

  if (error) {
    // Non-critique : badge compteur. Fallback silencieux (0) — pas de log
    // pour ne pas polluer la console (notamment en E2E lors du teardown).
    return 0;
  }

  return count || 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE NOTIFICATION
// ─────────────────────────────────────────────────────────────────────────────
export async function createNotification(
  orgId: string,
  projectId: string,
  recipientMemberId: string,
  type: NotificationType,
  entityType: EntityType,
  entityId: string,
  payload: NotificationPayload
): Promise<ProjectNotification | null> {
  const { error } = await supabase
    .from('project_notifications')
    .insert({
      org_id: orgId,
      project_id: projectId,
      recipient_member_id: recipientMemberId,
      type,
      entity_type: entityType,
      entity_id: entityId,
      payload: payload as unknown as Json,
    });

  if (error) {
    console.error('Error creating notification:', error);
    return null;
  }

  // Return a minimal object — the sender cannot SELECT the recipient's notification due to RLS
  return {
    id: '',
    orgId,
    projectId,
    recipientMemberId,
    type,
    entityType,
    entityId,
    payload,
    isRead: false,
    createdAt: new Date().toISOString(),
  } as ProjectNotification;
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE NOTIFICATIONS FOR MULTIPLE RECIPIENTS
// ─────────────────────────────────────────────────────────────────────────────
export async function createNotificationsForRecipients(
  orgId: string,
  projectId: string,
  recipientMemberIds: string[],
  type: NotificationType,
  entityType: EntityType,
  entityId: string,
  payload: NotificationPayload,
  excludeMemberId?: string
): Promise<void> {
  // Filter out the actor (don't notify yourself)
  const recipients = excludeMemberId
    ? recipientMemberIds.filter(id => id !== excludeMemberId)
    : recipientMemberIds;

  if (recipients.length === 0) return;

  const notifications = recipients.map(recipientMemberId => ({
    org_id: orgId,
    project_id: projectId,
    recipient_member_id: recipientMemberId,
    type,
    entity_type: entityType,
    entity_id: entityId,
    payload: payload as unknown as Json,
  }));

  const { error } = await supabase
    .from('project_notifications')
    .insert(notifications);

  if (error) {
    console.error('Error creating bulk notifications:', error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MARK NOTIFICATION AS READ
// ─────────────────────────────────────────────────────────────────────────────
export async function markAsRead(notificationId: string): Promise<boolean> {
  const { error } = await supabase
    .from('project_notifications')
    .update({ is_read: true })
    .eq('id', notificationId);

  if (error) {
    console.error('Error marking notification as read:', error);
    return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// MARK ALL NOTIFICATIONS AS READ
// ─────────────────────────────────────────────────────────────────────────────
export async function markAllAsRead(projectId?: string): Promise<boolean> {
  let query = supabase
    .from('project_notifications')
    .update({ is_read: true })
    .eq('is_read', false);

  if (projectId) {
    query = query.eq('project_id', projectId);
  }

  const { error } = await query;

  if (error) {
    console.error('Error marking all as read:', error);
    return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE NOTIFICATION
// ─────────────────────────────────────────────────────────────────────────────
export async function deleteNotification(notificationId: string): Promise<boolean> {
  const { error } = await supabase
    .from('project_notifications')
    .delete()
    .eq('id', notificationId);

  if (error) {
    console.error('Error deleting notification:', error);
    return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Map DB row to interface
// ─────────────────────────────────────────────────────────────────────────────
function mapNotification(row: any): ProjectNotification {
  return {
    id: row.id,
    orgId: row.org_id,
    projectId: row.project_id,
    recipientMemberId: row.recipient_member_id,
    type: row.type as NotificationType,
    entityType: row.entity_type as EntityType,
    entityId: row.entity_id,
    payload: (row.payload || {}) as Record<string, unknown>,
    isRead: row.is_read,
    createdAt: row.created_at,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// FORMAT NOTIFICATION MESSAGE
// ─────────────────────────────────────────────────────────────────────────────
export function formatNotificationMessage(notification: ProjectNotification): string {
  const { type, payload } = notification;
  const actor = (payload.actorName as string) || 'Quelqu\'un';
  const task = (payload.taskTitle as string) || 'une tâche';
  const project = (payload.projectName as string) || 'un projet';

  switch (type) {
    case 'assign':
      return `${actor} vous a assigné à "${task}"`;
    case 'unassign':
      return `${actor} vous a retiré de "${task}"`;
    case 'owner_change':
      return `${actor} vous a désigné responsable de "${task}"`;
    case 'comment':
      return `${actor} a commenté sur "${task}"`;
    case 'mention':
      return `${actor} vous a mentionné dans "${task}"`;
    case 'status_change':
      return `${actor} a changé le statut de "${task}" vers "${payload.newStatus || 'nouveau statut'}"`;
    case 'status_terminal':
      return `"${task}" a été marquée comme terminée`;
    case 'dependency_blocked':
      return `"${task}" est bloquée par "${payload.dependencyTaskTitle || 'une autre tâche'}"`;
    case 'dependency_resolved':
      return `"${task}" n'est plus bloquée`;
    case 'attachment_uploaded':
      return `${actor} a ajouté "${payload.fileName || 'un fichier'}" à "${task}"`;
    case 'watcher_added':
      return `${actor} vous a ajouté comme observateur de "${task}"`;
    case 'due_date_changed': {
      const newDate = payload.newValue || payload.dueDate;
      if (newDate) {
        return `${actor} a modifié l'échéance de "${task}"`;
      }
      return `${actor} a retiré l'échéance de "${task}"`;
    }
    case 'due_date_approaching': {
      const days = payload.daysRemaining || 0;
      if (days === 0) return `"${task}" arrive à échéance aujourd'hui`;
      if (days === 1) return `"${task}" arrive à échéance demain`;
      return `"${task}" arrive à échéance dans ${days} jours`;
    }
    case 'priority_changed':
      return `${actor} a changé la priorité de "${task}" en "${payload.newValue || 'nouvelle priorité'}"`;
    case 'milestone_assigned':
      return `${actor} a assigné "${task}" au jalon "${payload.milestoneName || 'un jalon'}"`;
    case 'milestone_completed':
      return `Le jalon "${payload.milestoneName || 'un jalon'}" a été complété`;
    case 'subtask_completed':
      return `${actor} a complété une sous-tâche de "${task}"`;
    case 'task_created':
      return `${actor} a créé la tâche "${task}"`;
    case 'task_deleted':
      return `${actor} a supprimé la tâche "${task}"`;
    case 'project_member_added':
      return `${actor} vous a ajouté au projet "${project}" en tant que ${payload.roleName || 'membre'}`;
    case 'project_member_removed':
      return `${actor} vous a retiré du projet "${project}"`;
    case 'project_role_changed':
      return `${actor} a changé votre rôle dans "${project}" en ${payload.newValue || payload.roleName || 'nouveau rôle'}`;
    case 'timesheet_approved': {
      const count = (payload.entryCount as number) || 1;
      return `${actor} a approuvé ${count > 1 ? `${count} entrées de` : 'votre entrée de'} feuille de temps`;
    }
    case 'timesheet_rejected': {
      const count = (payload.entryCount as number) || 1;
      const reason = (payload.rejectionReason as string);
      const base = `${actor} a rejeté ${count > 1 ? `${count} entrées de` : 'votre entrée de'} feuille de temps`;
      return reason ? `${base} : "${reason}"` : base;
    }
    case 'timesheet_submitted': {
      const count = (payload.entryCount as number) || 1;
      return `${actor} a soumis ${count > 1 ? `${count} entrées de` : 'une entrée de'} feuille de temps`;
    }
    case 'estimation_changed':
      return `${actor} a modifié l'estimation de "${task}"`;
    default:
      return `Nouvelle notification pour "${task}"`;
  }
}
